import { CharComp } from '@/components/CharComp'
import { EntrustOrderCoinInfo } from '@/components/EntrustOrderCoinInfo'
import { CustomTableLayout } from '@/layouts/CustomTableLayout'
import classNames from 'classnames'
import { ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { useResponsive, useSpotOrderListStore } from 'store'
import { EmptyData, FullScreenMinHeight, HocElTable, ListCell } from 'ui-components'
import { DefineComponentProps } from 'utils'
import '../styles/current-order-list-layout.scss'
import { CancelOrder } from './CancelOrder'

type CurrentOrderListProps = {
  currentSymbolId?: SymbolId
}

export const CurrentOrderList = defineComponent<CurrentOrderListProps>({
  props: DefineComponentProps<CurrentOrderListProps>(['currentSymbolId']),
  setup(props) {
    const { t } = useI18n()
    const spotOrderListStore = useSpotOrderListStore()
    const responsive = useResponsive()

    const currentOrderList = computed(() => {
      return spotOrderListStore.currentOrderList.filter((item) => {
        if (!spotOrderListStore.hideOtherOrder) return true
        if (!props.currentSymbolId) return true
        return item.symbolId === props.currentSymbolId
      })
    })

    return () => {
      if (responsive.isMobile) {
        return (
          <div class="current-order-list-layout">
            <div>
              <div class="mobile-order-list">
                {currentOrderList.value.map((item) => {
                  return (
                    <div class="mobile-order-list-item">
                      <EntrustOrderCoinInfo order={item} />
                      <div class="mobile-order-list-item-content">
                        <ListCell title={t('数量')} description={item.num.formatNumberWithCommas()} />
                        <ListCell title={t('委托价格')} description={item.price.formatNumberWithCommas()} />
                        <ListCell title={t('委托时间')} description={item.createdAt} />
                      </div>
                    </div>
                  )
                })}
              </div>
              {currentOrderList.value.length === 0 && <EmptyData />}
            </div>
          </div>
        )
      }
      return (
        <div class="current-order-list-layout">
          <CustomTableLayout>
            <HocElTable data={currentOrderList.value} class={'has-admin'}>
              {{
                default: () => (
                  <>
                    <ElTableColumn label={t('下单时间')} prop="createdAt">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return <CharComp>{data.row.createdAt}</CharComp>
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('交易对')} prop="symbolName">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return <CharComp>{data.row.symbolName}</CharComp>
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('方向')} prop="side">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return (
                            <CharComp
                              class={classNames({
                                up: data.row.side === 'buy',
                                down: data.row.side === 'sell',
                              })}
                            >
                              {data.row.side === 'buy' ? t('买入') : t('卖出')}
                            </CharComp>
                          )
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('委托价格')} prop="price">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return <CharComp>{data.row.price.formatNumberWithCommas()}</CharComp>
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('委托数量')} prop="num">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return (
                            <CharComp>
                              {data.row.num.formatNumberWithCommas()}&nbsp;
                              {spotOrderListStore.getCoinFromSymbolName(data.row.coinFrom)}
                            </CharComp>
                          )
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('委托总额')} prop="total">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return (
                            <CharComp>
                              {data.row.total.formatNumberWithCommas().decimalPadZero(2)}&nbsp;
                              {spotOrderListStore.getCoinFromSymbolName(data.row.coinTo)}
                            </CharComp>
                          )
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn label={t('操作')} prop="admin">
                      {{
                        default: (data: { row: ApiResponse.SpotOrderItem }) => {
                          return <CancelOrder order={data.row} />
                        },
                      }}
                    </ElTableColumn>
                  </>
                ),
                empty: () => (
                  <FullScreenMinHeight>
                    <EmptyData />
                  </FullScreenMinHeight>
                ),
              }}
            </HocElTable>
          </CustomTableLayout>
        </div>
      )
    }
  },
})
